import {IVanillaReaderPublicationDataStore} from "../VanillaReaderDataStore/IVanillaReaderPublicationDataStore";
import {IVanillaReaderHighlightDataStore} from "../VanillaReaderDataStore/IVanillaReaderHighlightDataStore";
import {IVanillaReaderBookmarkDataStore} from "../VanillaReaderDataStore/IVanillaReaderBookmarkDataStore";
import {
    IVanillaReaderStreamedResourceStorage,
    VanillaReaderStreamedResourceIndexDbStorage
} from "../VanillaReaderDataStore/VanillaReaderStreamedResourceIndexDbStorage";
import {IVanillaReaderOptionsDataStore} from "../VanillaReaderDataStore/IVanillaReaderOptionsDataStore";
import {ILegacyVanillaReaderPublicationStateData} from "../VanillaReader/VanillaReaderModelLegacy";
import {VanillaReaderOptionsIndexedDbDataStore} from "../VanillaReaderDataStore/VanillaReaderOptionsIndexedDbDataStore";
import {
    VanillaReaderPublicationIndexedDbDataStore
} from "../VanillaReaderDataStore/VanillaReaderPublicationIndexedDbDataStore";
import {
    VanillaReaderBookmarkIndexedDbDataStore
} from "../VanillaReaderDataStore/VanillaReaderBookmarkIndexedDbDataStore";
import {
    VanillaReaderHighlightIndexedDbDataStore
} from "../VanillaReaderDataStore/VanillaReaderHighlightIndexedDbDataStore";
import {
    IVanillaReaderUiOptionsData,
    VanillaReaderUiActiveOptionsPresetData,
    VanillaReaderUiOptionsPresetNames
} from "../VanillaReaderUI/VanillaReaderUiModel";
import {IVanillaReaderOptionsData, IVanillaReaderPublicationData,} from "../VanillaReader/VanillaReaderModel";
import {VanillaReaderLocalStorageKeys} from "../VanillaReaderDataStore/VanillaReaderStoreModel";

export class VanillaReaderAppStorage {
    private readonly _vanillaReaderPublicationDataStore: IVanillaReaderPublicationDataStore;
    private readonly _vanillaReaderHighlightStorage: IVanillaReaderHighlightDataStore;
    private readonly _vanillaReaderBookmarkStorage: IVanillaReaderBookmarkDataStore;
    private readonly _vanillaReaderStreamedResourceStorage: IVanillaReaderStreamedResourceStorage;
    private readonly _vanillaReaderOptionsDataStore: IVanillaReaderOptionsDataStore;

    constructor() {

        // Storage client for storage of application options for UI and reading.
        this._vanillaReaderOptionsDataStore = new VanillaReaderOptionsIndexedDbDataStore('options.vanillareader.colibrio.com');

        // Storage client for storage of publication metadata.
        this._vanillaReaderPublicationDataStore = new VanillaReaderPublicationIndexedDbDataStore('publication.vanillareader.colibrio.com');

        // Storage client for storage of bookmark data.
        this._vanillaReaderBookmarkStorage = new VanillaReaderBookmarkIndexedDbDataStore('bookmarks.vanillareader.colibrio.com');

        // Storage client for storage of highlight data.
        this._vanillaReaderHighlightStorage = new VanillaReaderHighlightIndexedDbDataStore('highlights.vanillareader.colibrio.com');

        // Storage client for storage of downloaded publication resource data. This is used for offline storage.
        this._vanillaReaderStreamedResourceStorage = new VanillaReaderStreamedResourceIndexDbStorage('data.vanillareader.colibrio.com');

    }

    public async initialize(): Promise<void[]> {
        return Promise.all([
            this._vanillaReaderBookmarkStorage.initialize(),
            this._vanillaReaderHighlightStorage.initialize(),
            this._vanillaReaderStreamedResourceStorage.initialize(),
            this._vanillaReaderPublicationDataStore.initialize(),
            this._vanillaReaderOptionsDataStore.initialize(),
        ]);
    }

    public get publicationDataStore(): IVanillaReaderPublicationDataStore {
        return this._vanillaReaderPublicationDataStore;
    }

    public get highlightDataStore(): IVanillaReaderHighlightDataStore {
        return this._vanillaReaderHighlightStorage;
    }

    public get bookmarkDataStore(): IVanillaReaderBookmarkDataStore {
        return this._vanillaReaderBookmarkStorage;
    }

    public get streamedResourcesDataStore(): IVanillaReaderStreamedResourceStorage {
        return this._vanillaReaderStreamedResourceStorage;
    }

    public get optionsDataStore(): IVanillaReaderOptionsDataStore {
        return this._vanillaReaderOptionsDataStore;
    }

    /*
    *
    * This function saves the current application state by calling `serializeAppState` on the `VanillaReader` instance, and
    * then saving it to the device using the active `IVanillaDataStore` instance (currently `VanillaReaderLocalStorageDataStore`).
    *
     */
    async saveVanillaReaderAppOptionsState(readerOptionsData: IVanillaReaderOptionsData, uiOptionsData: IVanillaReaderUiOptionsData): Promise<void> {

        if (readerOptionsData) {
            if (readerOptionsData.viewOptions) {
                await this._vanillaReaderOptionsDataStore.setVanillaReaderOptionsData(readerOptionsData);
            }
        }

        if (uiOptionsData) {
            this.saveVanillaReaderUiOptions(uiOptionsData).catch(console.warn);
        }
    }

    // Save the UI settings to the data store.
    async saveVanillaReaderUiOptions(options: IVanillaReaderUiOptionsData): Promise<void> {
        await this._vanillaReaderOptionsDataStore.setVanillaReaderUiOptionsData(options);
    }

    getActiveVanillaReaderUiOptionsPresetData(): VanillaReaderUiActiveOptionsPresetData | undefined {
        let optionsPresetData = localStorage.getItem(VanillaReaderLocalStorageKeys.VanillaReaderUiActiveOptionsPreset);

        if(!optionsPresetData) {
            return;
        }

        return JSON.parse(optionsPresetData);

    }

    setActiveVanillaReaderUiOptionsPresetData(presetData: VanillaReaderUiActiveOptionsPresetData): void {
        localStorage.setItem(VanillaReaderLocalStorageKeys.VanillaReaderUiActiveOptionsPreset, JSON.stringify(presetData));
    }

    clearActiveVanillaReaderUiOptionsPresetData(): void {
        localStorage.removeItem(VanillaReaderLocalStorageKeys.VanillaReaderUiActiveOptionsPreset);
    }

    async clearAllPublicationCacheData(): Promise<void> {
        let publicationData = await this._vanillaReaderPublicationDataStore.fetchAllPublicationData();
        publicationData.forEach((data: IVanillaReaderPublicationData)=>{
            this._vanillaReaderPublicationDataStore.deletePublicationCacheData(data.id).then((noAffected:number)=>{
                console.log(`deletePublicationCacheData returned ${noAffected}`);
            }).catch(console.warn);
        });
    }

    // This method is used to migrate data from the older, less persistent storage that used the LocalStorage API.
    async performDataMigrationFromLocalStorage(): Promise<number> {
        let numberOfItemsMigrated: number = 0;

        // As LocalStorage is a simple key value store with no "namespacing", we need to loop through all items in the store
        // and determine if we should migrate the item or not.
        for (const key in localStorage) {
            let data: ILegacyVanillaReaderPublicationStateData | undefined;
            let item = localStorage.getItem(key);

            if (item) {
                try {
                    data = JSON.parse(item);
                } catch (e) {
                    continue;
                }

                if (data === undefined) {
                    continue;
                }

                if (this._isLegacyVanillaReaderPublicationStateData(data) && data.id) {

                    // Check if the publication has already been migrated.
                    if (await this._vanillaReaderPublicationDataStore.hasPublicationData(data.id)) {
                        console.log(`performDataMigrationFromLocalStorage: "${data.fileName}" has already been migrated.`);
                        continue;
                    }

                    console.log(`performDataMigrationFromLocalStorage: Starting migration for publication "${data.fileName}"`);

                    await this._vanillaReaderPublicationDataStore.setPublicationData(data.id, {
                        id: data.id,
                        fileName: data.fileName,
                        coverImageAsBase64: data.coverImageAsBase64,
                        fileSourceUri: data.fileSourceUri,
                        format: data.fileSourceUri,
                        hasMoSyncMedia: data.hasMoSyncMedia,
                        isScripted: data.isScripted,
                        isFixedLayout: data.isFixedLayout,
                        isRtl: data.isRtl,
                        isAudiobook: data.isAudiobook,
                        metadata: data.metadata,
                        url: data.url,
                        title: data.title,
                    }).catch(console.warn);

                    console.log(`performDataMigrationFromLocalStorage: Migrated publication data for "${data.fileName}"`);

                    if (data.highlights && data.highlights.length > 0) {
                        for (const highlight of data.highlights) {
                            highlight.publicationId = data.id;
                            await this._vanillaReaderHighlightStorage.addHighlight(highlight).catch(console.warn);
                        }
                        console.log(`performDataMigrationFromLocalStorage: Migrated ${data.highlights.length} highlights`);
                    }

                    if (data.bookmarks && data.bookmarks.length > 0) {
                        for (const bookmark of data.bookmarks) {
                            bookmark.publicationId = data.id;
                            await this._vanillaReaderBookmarkStorage.addBookmark(bookmark).catch(console.warn);
                        }
                        console.log(`performDataMigrationFromLocalStorage: Migrated ${data.bookmarks.length} bookmarks`);
                    }

                    if (data.latestReadingPosition) {
                        await this._vanillaReaderPublicationDataStore.setPublicationReadingPositionData(
                            data.id!,
                            {
                                publicationId: data.id,
                                timestamp: data.latestReadingDateTime || 0,
                                locator: data.latestReadingPosition.locator,
                                navItemData: data.latestReadingPosition.navItemData,
                                documentIndex: data.latestReadingPosition.documentIndex,
                            },
                        ).catch(console.warn);
                        console.log(`performDataMigrationFromLocalStorage: Migrated latest reading position data.`);
                    }

                    numberOfItemsMigrated++;

                }

                console.log(`performDataMigrationFromLocalStorage: Done migrating publication "${data.fileName}"`);
            }
        }

        window.localStorage.setItem(
            VanillaReaderLocalStorageKeys.VanillaReaderAppDataMigrationDateStamp,
            Date.now().toString()
        );

        return numberOfItemsMigrated;
    }

    _isVanillaReaderUiOptionsPresetName(name: string): name is VanillaReaderUiOptionsPresetNames {
        return (VanillaReaderUiOptionsPresetNames as object).hasOwnProperty(name);
    }

    _isLegacyVanillaReaderPublicationStateData(data: object): data is ILegacyVanillaReaderPublicationStateData {
        return (data.hasOwnProperty('id') && data.hasOwnProperty('fileName'));
    }
}
